import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import Avatar from "@mui/material/Avatar"
import Tooltip from "@mui/material/Tooltip"
import TextField from "@mui/material/TextField"
import CallIcon from "@mui/icons-material/Call"
import CallEndIcon from "@mui/icons-material/CallEnd"
import MicIcon from "@mui/icons-material/Mic"
import MicOffIcon from "@mui/icons-material/MicOff"
import ScreenShareIcon from "@mui/icons-material/ScreenShare"
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare"
import GroupsIcon from "@mui/icons-material/Groups"
import MinimizeIcon from "@mui/icons-material/Minimize"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import SendIcon from "@mui/icons-material/Send"

const MONO = "var(--app-font-mono)"
const PRIVATE_COLOR = "var(--app-success)"
const GROUP_COLOR = "#7c83ff"

function syncPopupStyles(targetDocument) {
  const liveKeys = new Set()
  Array.from(document.styleSheets).forEach((sheet, index) => {
    const key = String(index)
    liveKeys.add(key)
    let copy = targetDocument.head.querySelector(`[data-call-style-copy="${key}"]`)
    if (sheet.href) {
      if (copy?.tagName !== "LINK") { copy?.remove(); copy = targetDocument.createElement("link") }
      copy.rel = "stylesheet"
      copy.href = sheet.href
      copy.dataset.callStyleCopy = key
      if (!copy.isConnected) targetDocument.head.appendChild(copy)
      return
    }
    try {
      const css = Array.from(sheet.cssRules || [], (rule) => rule.cssText).join("\n")
      if (!css) return
      if (copy?.tagName !== "STYLE") { copy?.remove(); copy = targetDocument.createElement("style") }
      copy.dataset.callStyleCopy = key
      if (copy.textContent !== css) copy.textContent = css
      if (!copy.isConnected) targetDocument.head.appendChild(copy)
    } catch { /* inaccessible stylesheet; linked sheets are copied above */ }
  })
  targetDocument.querySelectorAll("[data-call-style-copy]").forEach((node) => {
    if (!liveKeys.has(node.dataset.callStyleCopy)) node.remove()
  })
}

export function CallButton({ call, target, show }) {
  if (!show) return null
  const idle = call.status === "idle" && call.groupCall.status === "idle"
  return (
    <Tooltip title={idle ? "Start voice call" : "On a call"} arrow placement="top">
      <span style={{ marginLeft: "auto" }}>
        <IconButton size="small" disabled={!idle} onClick={() => call.startCall(target)}
          sx={{ mr: 0.5, color: PRIVATE_COLOR, "&:hover": { color: "#16a34a" },
            "&.Mui-disabled": { color: "rgba(var(--app-fg-rgb),0.18)" }, flexShrink: 0 }}>
          <CallIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

export function GroupCallButton({ call, group, show }) {
  if (!show || !group) return null
  const idle = call.status === "idle" && call.groupCall.status === "idle"
  return (
    <Tooltip title={idle ? "Call everyone in this group" : "On a call"} arrow placement="top">
      <span>
        <IconButton size="small" disabled={!idle} onClick={() => call.startGroupCall(group)}
          sx={{ color: GROUP_COLOR, "&.Mui-disabled": { color: "rgba(var(--app-fg-rgb),0.18)" } }}>
          <GroupsIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

function StreamVideo({ share, focused, own, onFocus, onStop }) {
  const ref = useRef(null)
  useEffect(() => { if (ref.current) ref.current.srcObject = share.stream }, [share.stream])
  return (
    <Box onClick={onFocus} sx={{ position: "relative", width: "100%", height: "100%", minWidth: 0,
      minHeight: 0, bgcolor: "#000", borderRadius: 1, overflow: "hidden", cursor: "pointer",
      border: focused ? "2px solid var(--app-info)" : "1px solid rgba(var(--app-fg-rgb),0.12)" }}>
      <Box component="video" ref={ref} autoPlay playsInline muted={own}
        sx={{ display: "block", width: "100%", height: "100%", maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
      <Typography sx={{ position: "absolute", left: 8, bottom: 6, px: 0.6, py: 0.2,
        borderRadius: 0.5, bgcolor: "rgba(var(--app-shadow-rgb),0.72)", color: "#fff", fontFamily: MONO, fontSize: "0.58rem" }}>
        {own ? "Your screen" : `${share.owner}'s screen`}
      </Typography>
      {own && (
        <Tooltip title="Stop sharing this screen" arrow>
          <IconButton size="small" onClick={event => { event.stopPropagation(); onStop() }}
            sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(var(--app-shadow-rgb),0.72)", color: "var(--app-error)",
              "&:hover": { bgcolor: "rgba(40,0,0,0.9)" } }}>
            <StopScreenShareIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

function CallChat({ chat, color }) {
  const endRef = useRef(null)
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" })
  }, [chat.messages])
  return (
    <Box sx={{ minWidth: 240, minHeight: 0, display: "flex", flexDirection: "column",
      borderLeft: "1px solid rgba(var(--app-fg-rgb),0.08)", bgcolor: "var(--app-bg)" }}>
      <Typography sx={{ px: 1.25, py: 1, borderBottom: "1px solid rgba(var(--app-fg-rgb),0.07)",
        color, fontFamily: MONO, fontSize: "0.68rem", fontWeight: 700 }}>
        {chat.title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.2, py: 1 }}>
        {!chat.messages.length && (
          <Typography sx={{ mt: 2, textAlign: "center", color: "rgba(var(--app-fg-rgb),0.28)", fontFamily: MONO, fontSize: "0.62rem" }}>
            No messages yet.
          </Typography>
        )}
        {chat.messages.map(message => {
          const sender = message.username ?? message.from ?? "?"
          const mine = sender === chat.me
          const label = chat.profiles?.[sender]?.display_name ?? sender
          return (
            <Box key={message.id} sx={{ mb: 1, ml: mine ? 2 : 0, mr: mine ? 0 : 2 }}>
              <Typography sx={{ color: mine ? color : "rgba(var(--app-fg-rgb),0.5)", fontFamily: MONO, fontSize: "0.54rem", mb: 0.2 }}>
                {mine ? "You" : label}
              </Typography>
              <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: mine ? `color-mix(in srgb, ${color} 12.5%, transparent)` : "rgba(var(--app-fg-rgb),0.055)",
                border: `1px solid ${mine ? `color-mix(in srgb, ${color} 20.8%, transparent)` : "rgba(var(--app-fg-rgb),0.06)"}` }}>
                {message.text && <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "rgba(var(--app-fg-rgb),0.88)", fontSize: "0.67rem" }}>{message.text}</Typography>}
                {message.image && <Box component="img" src={message.image} sx={{ display: "block", mt: message.text ? 0.6 : 0, maxWidth: "100%", maxHeight: 180, borderRadius: 0.75 }} />}
                {message.file_name && <Typography sx={{ color, fontFamily: MONO, fontSize: "0.6rem" }}>Attachment · {message.file_name}</Typography>}
                {message.voice && <Typography sx={{ color, fontFamily: MONO, fontSize: "0.6rem" }}>Voice message</Typography>}
              </Box>
            </Box>
          )
        })}
        <span ref={endRef} />
      </Box>
      <Box sx={{ p: 0.8, display: "flex", alignItems: "center", gap: 0.5, borderTop: "1px solid rgba(var(--app-fg-rgb),0.07)" }}>
        <TextField fullWidth size="small" multiline maxRows={3} value={chat.input} onChange={chat.onInputChange}
          onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); chat.onSend() } }}
          placeholder={`Message ${chat.title}…`}
          sx={{ "& .MuiInputBase-input": { color: "#fff", fontFamily: MONO, fontSize: "0.67rem" },
            "& fieldset": { borderColor: "rgba(var(--app-fg-rgb),0.12)" } }} />
        <IconButton size="small" disabled={!chat.input.trim()} onClick={chat.onSend} sx={{ color }}>
          <SendIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  )
}

// The chat-toggle icon's position within NavRail varies by page (some nav items are
// SSH-gated) — so the minimized call badge anchors to its live DOM rect instead of a
// guessed fixed offset, which used to land outside the sidebar on some pages.
function useChatToggleRect(active) {
  const [rect, setRect] = useState(null)
  useEffect(() => {
    if (!active) return
    const update = () => {
      const el = document.querySelector("[data-chat-toggle]")
      setRect(el ? el.getBoundingClientRect() : null)
    }
    update()
    window.addEventListener("resize", update)
    const id = setInterval(update, 500)
    return () => { window.removeEventListener("resize", update); clearInterval(id) }
  }, [active])
  return rect
}

const statusText = (call, groupMode) => {
  if (groupMode) {
    if (call.groupCall.status === "ringing") return "Incoming group call"
    if (call.groupCall.status === "calling") return `Calling ${call.groupCall.pending.length} member${call.groupCall.pending.length === 1 ? "" : "s"}…`
    if (call.groupCall.status === "connecting") return "Joining call…"
    return `${call.groupCall.peers.length + 1} participant${call.groupCall.peers.length ? "s" : ""}`
  }
  if (call.status === "ringing") return "Incoming call"
  if (call.status === "calling") return "Calling…"
  if (call.status === "connecting") return "Connecting…"
  return "Connected"
}

const CallOverlay = forwardRef(function CallOverlay({ call, displayName, avatar, chat }, ref) {
  const groupMode = call.groupCall.status !== "idle"
  const active = call.status !== "idle" || groupMode
  const ringing = groupMode ? call.groupCall.status === "ringing" : call.status === "ringing"
  const connected = groupMode ? call.groupCall.status === "in-call" : call.status === "in-call"
  const color = groupMode ? GROUP_COLOR : PRIVATE_COLOR
  const [minimized, setMinimized] = useState(false)
  const toggleRect = useChatToggleRect(minimized)
  const [maximized, setMaximized] = useState(false)
  const [hideOwn, setHideOwn] = useState(false)
  const [focusedId, setFocusedId] = useState(null)
  const [popupRoot, setPopupRoot] = useState(null)
  const popupRef = useRef(null)
  const wasActiveRef = useRef(false)

  const allShares = [...call.remoteScreenShares, ...(hideOwn ? [] : call.localScreenShares)]
  const focused = allShares.find(share => share.id === focusedId)

  useEffect(() => {
    if (active && !wasActiveRef.current) setMinimized(false)
    if (!active && wasActiveRef.current) {
      const popup = popupRef.current
      popupRef.current = null
      setPopupRoot(null)
      if (typeof popup?.close === "function") popup.close()
      setFocusedId(null)
      setMaximized(false)
    }
    wasActiveRef.current = active
  }, [active])

  useEffect(() => {
    if (!popupRoot) return
    const id = setInterval(() => {
      if (popupRef.current?.closed) {
        popupRef.current = null
        setPopupRoot(null)
      }
    }, 400)
    return () => clearInterval(id)
  }, [popupRoot])

  useEffect(() => {
    if (!call.note) return
    const timer = setTimeout(() => call.clearNote?.(), 3500)
    return () => clearTimeout(timer)
  }, [call.note, call.clearNote])

  useEffect(() => {
    if (focusedId && !allShares.some(share => share.id === focusedId)) setFocusedId(null)
  }, [allShares, focusedId])

  useEffect(() => () => {
    const popup = popupRef.current
    popupRef.current = null
    if (typeof popup?.close === "function") popup.close()
  }, [])

  // MUI/Emotion inserts call-specific rules after the blank window is created.
  // Re-copy the live CSSOM after each portal commit so the pop-out matches the dialog.
  useLayoutEffect(() => {
    if (popupRoot?.ownerDocument) syncPopupStyles(popupRoot.ownerDocument)
  })

  useEffect(() => {
    if (!popupRef.current || popupRef.current.closed || !active) return
    popupRef.current.document.title = groupMode
      ? (call.groupCall.group?.name || "Group call")
      : `Call with ${displayName || call.peer || "contact"}`
  }, [active, groupMode, call.groupCall.group?.name, call.peer, displayName])

  const openPopout = () => {
    if (popupRef.current && !popupRef.current.closed) { popupRef.current.focus(); return }
    const popup = window.open("", "livechat-call", "popup=yes,width=1200,height=760,resizable=yes")
    if (!popup) return
    popup.document.title = groupMode ? "Group call" : `Call with ${displayName || call.peer}`
    Object.assign(popup.document.body.style, {
      margin: "0", background: "#07070d", overflow: "hidden",
    })
    const root = popup.document.createElement("div")
    Object.assign(root.style, {
      width: "100vw", height: "100vh", padding: "16px", boxSizing: "border-box",
      display: "flex", alignItems: "center", justifyContent: "center",
    })
    popup.document.body.appendChild(root)
    popupRef.current = popup
    setPopupRoot(root)
  }

  useImperativeHandle(ref, () => ({ openPopout }), [groupMode, displayName, call.peer])

  if (!active) return null

  const name = groupMode ? (call.groupCall.group?.name || "Group call") : (displayName || call.peer)
  const contents = (
    <Box data-chat-overlay style={popupRoot ? {
      position: "relative", left: "auto", top: "auto", width: "min(1180px, 100%)",
      height: "min(760px, 100%)", minWidth: 0, minHeight: 0, resize: "none",
    } : undefined} sx={{
      position: "fixed", left: maximized ? 72 : "10vw", top: maximized ? 8 : "8vh",
      width: maximized ? "calc(100vw - 80px)" : "min(1180px, 82vw)", height: maximized ? "calc(100vh - 16px)" : "min(760px, 82vh)",
      minWidth: 640, minHeight: 420, resize: maximized ? "none" : "both", overflow: "hidden", zIndex: 9100,
      bgcolor: "#080810", border: `1px solid color-mix(in srgb, ${color} 39.6%, transparent)`, borderRadius: 1.5, boxShadow: "0 20px 70px rgba(var(--app-shadow-rgb),0.78)",
      display: minimized ? "none" : "flex", flexDirection: "column",
    }}>
      <Box sx={{ height: 48, px: 1.25, display: "flex", alignItems: "center", gap: 1,
        borderBottom: "1px solid rgba(var(--app-fg-rgb),0.08)", bgcolor: "#10101c", flexShrink: 0 }}>
        <Avatar src={groupMode ? undefined : avatar || undefined} sx={{ width: 30, height: 30, bgcolor: `color-mix(in srgb, ${color} 15.7%, transparent)`, color, fontSize: "0.75rem" }}>
          {groupMode ? <GroupsIcon sx={{ fontSize: 17 }} /> : (name?.[0] || "?").toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ color: "#fff", fontFamily: MONO, fontSize: "0.72rem" }}>{name}</Typography>
          <Typography sx={{ color: connected ? PRIVATE_COLOR : "rgba(var(--app-fg-rgb),0.5)", fontFamily: MONO, fontSize: "0.57rem" }}>
            {statusText(call, groupMode)}
          </Typography>
        </Box>
        {!!call.localScreenShares.length && (
          <Tooltip title={hideOwn ? "Show your screen shares" : "Hide your screen shares"} arrow>
            <IconButton size="small" onClick={() => setHideOwn(value => !value)} sx={{ color: hideOwn ? "rgba(var(--app-fg-rgb),0.4)" : color }}>
              {hideOwn ? <VisibilityOffIcon sx={{ fontSize: 17 }} /> : <VisibilityIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
        )}
        {connected && navigator.mediaDevices?.getDisplayMedia && (
          <Tooltip title="Share another screen or window" arrow><IconButton size="small" onClick={call.addScreenShare} sx={{ color: "var(--app-info)" }}><ScreenShareIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
        )}
        <Tooltip title="Open call in a separate browser window" arrow><IconButton size="small" onClick={openPopout} sx={{ color: "rgba(var(--app-fg-rgb),0.65)" }}><OpenInNewIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
        {!popupRoot && <Tooltip title={maximized ? "Restore dialog" : "Maximize dialog"} arrow><IconButton size="small" onClick={() => setMaximized(value => !value)} sx={{ color: "rgba(var(--app-fg-rgb),0.65)" }}>{maximized ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}</IconButton></Tooltip>}
        {!popupRoot && <Tooltip title="Minimize call" arrow><IconButton size="small" onClick={() => setMinimized(true)} sx={{ color: "rgba(var(--app-fg-rgb),0.65)" }}><MinimizeIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(240px, 30%)" }}>
        <Box sx={{ minWidth: 0, minHeight: 0, p: 1, display: "flex", flexDirection: "column", bgcolor: "#050509" }}>
          {allShares.length ? (
            focused ? (
              <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                <StreamVideo share={focused} focused own={call.localScreenShares.some(share => share.id === focused.id)}
                  onFocus={() => setFocusedId(null)} onStop={() => call.stopScreenShare(focused.id)} />
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: "grid", gap: 1,
                gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(allShares.length))}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${Math.ceil(allShares.length / Math.ceil(Math.sqrt(allShares.length)))}, minmax(0, 1fr))` }}>
                {allShares.map(share => <StreamVideo key={share.id} share={share} own={call.localScreenShares.some(item => item.id === share.id)}
                  onFocus={() => setFocusedId(share.id)} onStop={() => call.stopScreenShare(share.id)} />)}
              </Box>
            )
          ) : (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: `color-mix(in srgb, ${color} 12.5%, transparent)`, color }}>
                {groupMode ? <GroupsIcon sx={{ fontSize: 34 }} /> : <CallIcon sx={{ fontSize: 34 }} />}
              </Avatar>
              <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.45)", fontFamily: MONO, fontSize: "0.67rem", textAlign: "center" }}>
                {connected ? "No screens are being shared" : statusText(call, groupMode)}
              </Typography>
            </Box>
          )}
          <Box sx={{ height: 58, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flexShrink: 0 }}>
            {ringing ? (
              <>
                <IconButton onClick={groupMode ? call.acceptGroupCall : call.accept} sx={{ bgcolor: PRIVATE_COLOR, color: "#06140a", "&:hover": { bgcolor: "#16a34a" } }}><CallIcon /></IconButton>
                <IconButton onClick={groupMode ? call.leaveGroupCall : call.decline} sx={{ bgcolor: "var(--app-error)", color: "#fff", "&:hover": { bgcolor: "#dc2626" } }}><CallEndIcon /></IconButton>
              </>
            ) : (
              <>
                <IconButton onClick={groupMode ? call.toggleGroupMute : call.toggleMute}
                  sx={{ color: (groupMode ? call.groupMuted : call.muted) ? "var(--app-error)" : "#fff", border: "1px solid rgba(var(--app-fg-rgb),0.15)" }}>
                  {(groupMode ? call.groupMuted : call.muted) ? <MicOffIcon /> : <MicIcon />}
                </IconButton>
                <IconButton onClick={groupMode ? call.leaveGroupCall : call.hangup} sx={{ bgcolor: "var(--app-error)", color: "#fff", "&:hover": { bgcolor: "#dc2626" } }}><CallEndIcon /></IconButton>
              </>
            )}
          </Box>
        </Box>
        <CallChat chat={chat} color={color} />
      </Box>
    </Box>
  )

  if (popupRoot) return createPortal(contents, popupRoot)
  return (
    <>
      {minimized && (
        <IconButton data-chat-overlay aria-label={groupMode ? "Open group call" : "Open call"}
          onClick={() => setMinimized(false)} sx={{ position: "fixed",
            left: toggleRect ? toggleRect.left + toggleRect.width / 2 - 20 : 12,
            top: toggleRect ? toggleRect.top - 46 : undefined,
            bottom: toggleRect ? undefined : 112,
            zIndex: 9200, width: 40, height: 40, bgcolor: `color-mix(in srgb, ${color} 14.1%, transparent)`, color, border: `1px solid color-mix(in srgb, ${color} 50.2%, transparent)`,
            boxShadow: `0 0 18px color-mix(in srgb, ${color} 20.8%, transparent)`, "&:hover": { bgcolor: `color-mix(in srgb, ${color} 22%, transparent)` } }}>
          <CallIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
      {contents}
    </>
  )
})

export default CallOverlay
